import { Activity, HardDrive } from 'lucide-react';
import { safeDistanceToNow } from '@/lib/safe-format';
import type { MdmDevice } from '../hooks/useStaffCustomerDevices';
import type { LiveDeviceStatus } from '../hooks/useDeviceHeartbeats';
import { classifyDeviceStatus } from './DeviceStatusDot';

const HEALTH_LABEL: Record<'online' | 'stale' | 'hdmiOff' | 'offline', string> = {
  online: 'Online',
  stale: 'Stale',
  hdmiOff: 'HDMI off',
  offline: 'Offline',
};

/**
 * Four-card health strip for the device detail drawer.
 *
 * Cards: Health, Storage, CPU, Memory.
 *
 * Note (Phase 1): the Device schema exposes `usedStorage` / `totalStorage`
 * but does NOT currently return CPU or Memory metrics. Those two cards
 * render "—" until a telemetry pipeline lands (Phase 3). When that ships,
 * wire the new fields here and drop the placeholders.
 */

function formatBytes(bytes: number | undefined): string | null {
  if (!bytes || !Number.isFinite(bytes) || bytes <= 0) return null;
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let size = bytes;
  let i = 0;
  while (size >= 1024 && i < units.length - 1) {
    size /= 1024;
    i += 1;
  }
  return `${size.toFixed(size >= 10 ? 0 : 1)} ${units[i]}`;
}

interface HealthCardProps {
  title: string;
  value: React.ReactNode;
  sub?: string;
  icon?: React.ReactNode;
  testId?: string;
}

function HealthCard({ title, value, sub, icon, testId }: HealthCardProps) {
  return (
    <div data-testid={testId} className="rounded-lg border bg-card p-3 shadow-sm min-w-0">
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        {icon}
        <span className="truncate">{title}</span>
      </div>
      <div className="mt-1 text-sm font-semibold truncate">{value}</div>
      {sub && <div className="mt-0.5 text-xs text-muted-foreground truncate">{sub}</div>}
    </div>
  );
}

export function DeviceHealthStrip({
  device,
  liveStatus,
}: {
  device: MdmDevice;
  liveStatus?: LiveDeviceStatus;
}) {
  const status = liveStatus ?? classifyDeviceStatus(device);
  const healthLabel = HEALTH_LABEL[status];

  const used = formatBytes(device.usedStorage);
  const total = formatBytes(device.totalStorage);
  const storageValue = used && total ? `${used} / ${total}` : (used ?? total ?? '—');
  const storageSub =
    device.usedStorage && device.totalStorage && device.totalStorage > 0
      ? `${Math.round((device.usedStorage / device.totalStorage) * 100)}%`
      : undefined;

  const healthSub = device.lastHeartBeat
    ? safeDistanceToNow(device.lastHeartBeat, { addSuffix: true }, '—')
    : '—';

  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-4" data-testid="mdm-device-health-strip">
      <HealthCard
        testId="mdm-health-card-health"
        title={'Health'}
        value={healthLabel}
        sub={healthSub}
        icon={<Activity className="h-3.5 w-3.5" />}
      />
      <HealthCard
        testId="mdm-health-card-storage"
        title={'Storage'}
        value={storageValue}
        sub={storageSub}
        icon={<HardDrive className="h-3.5 w-3.5" />}
      />
      <HealthCard testId="mdm-health-card-cpu" title={'CPU'} value="—" sub={'No data yet'} />
      <HealthCard
        testId="mdm-health-card-memory"
        title={'Memory'}
        value="—"
        sub={'No data yet'}
      />
    </div>
  );
}
